/**
 * Splitting a long recording into parts the gateway can actually finish.
 *
 * One request has ~120 seconds of processing in it, and there is nowhere to
 * raise that: the next wall is Cloudflare at 125 s, which is not ours to
 * configure. Chunking sidesteps the ceiling instead of fighting it — each
 * part is a short request that comfortably fits, so the limit stops applying
 * rather than being pushed.
 *
 * It happens in the BROWSER, and that is the load-bearing decision. Cutting
 * server-side would not help: one request would still take N × the
 * processing time and hit the same two walls, so it would need asynchronous
 * delivery as well. And the audio is already here — the video was unpacked
 * here, the hash was computed here, the envelope is built here.
 *
 * The cut lands on a PAUSE, inside a two-minute window before the target, so
 * no subtitle line straddles a boundary and the parts need no stitching
 * beyond shifting their timestamps.
 */
import { isWma } from './asf'
import { envelopeOf } from './audioEnvelope'
import type { FFmpeg } from './ffmpegClient'
import { seekIsUnreliable, spansFromParts, type PlaybackAudio } from './playbackAudio'
import { MIN_PAUSE_FRAMES, SILENCE_LEVEL, type LoudnessEnvelope } from './subtitleSegment'

/** Longer than this and the file is cut. Below it nothing happens at all —
 * the same single request as before, byte for byte.
 *
 * It was 20 minutes, and 20 minutes does not hold. That is ~92 s of
 * processing at the 1:13 rate measured on a free gateway, plus the upload of
 * a 9 MB part, against a proxy that gives up around 100 s — so every part sat
 * on the line. Six of them (a two-hour file) usually got through; 27 of them
 * (8 h 42 m) did not, and the run died around the halfway mark with a 504.
 *
 * 15 minutes is ~69 s against that same ~100 s: about seven tenths of the
 * budget, leaving room for a gateway having a slow minute — and it needs it,
 * since a real stand took 190 s for 12 minutes of audio. It costs more requests — that file
 * becomes 35 parts instead of 27 — and requests are the cheap thing here. */
export const CHUNK_OVER_SECONDS = 15 * 60
/** Where a cut aims. */
export const CHUNK_TARGET_SECONDS = 15 * 60
/** The pause is looked for in [CHUNK_SEARCH_FROM, CHUNK_TARGET_SECONDS]
 * counted from the start of the part — two minutes of audio, a couple of
 * hundred kilobytes at 8 kHz mono. */
export const CHUNK_SEARCH_FROM = 13 * 60

/** Sample rate of the window we analyse — the same 8 kHz the envelope uses. */
const WINDOW_RATE = 8000
/** Ceiling on the bitrate of the parts that get uploaded. An hour of mono at
 * 64 kbit/s is 27 MB, so the 75 MB per-request cap is never in play. */
const MAX_PART_KBPS = 64
/** …and a floor, so a bad estimate cannot produce something unusable. */
const MIN_PART_KBPS = 8

/** What to re-encode the parts at: never more than the recording already has.
 *
 * Fixing this at 64 kbit/s inflated an 8 kbit/s dictation eightfold — a 30 MB
 * file left as 239 MB of uploads, in 39 requests, on whatever connection the
 * person happens to be on. Re-encoding above the source cannot add anything
 * that was not recorded; it only makes every request slower, which is the
 * thing that was timing out in the first place. */
export function partBitrateKbps(file: { size: number }, durationSeconds: number): number {
  const kbps = Math.round((file.size * 8) / durationSeconds / 1000)
  return Math.max(MIN_PART_KBPS, Math.min(MAX_PART_KBPS, kbps))
}

export function partBitrate(file: { size: number }, durationSeconds: number): string {
  return `${partBitrateKbps(file, durationSeconds)}k`
}
/** How many ffmpeg calls one instance is asked to make before it is replaced.
 *
 * ffmpeg.wasm does not give everything back between `exec` calls, and there
 * is no flushing it: on an 8 h 42 m recording — 35 parts, two calls each —
 * the run died with "memory access out of bounds" a couple of parts from the
 * end, having done all the work. A fresh instance every so often costs one
 * wasm compile and re-writing the input (the core itself is already in the
 * browser cache), which is nothing against losing the whole cut. */
const EXECS_PER_INSTANCE = 16

/** What a cut produced: the parts to upload, and — when there was more than
 * one — an audio file the player can actually seek. See `playbackAudio.ts`
 * for why the original will not do. */
export interface ChunkedAudio {
  chunks: AudioChunk[]
  playback: PlaybackAudio | null
}

export interface AudioChunk {
  file: File
  /** Where this part begins in the original recording, in seconds. The
   * gateway times each part from zero, so this is what the cues are shifted
   * by when the parts are stitched back together. */
  offset: number
}

export type ChunkProgress =
  | { phase: 'load' }
  | { phase: 'cut'; done: number; total: number }

/** The longest recording accepted at all: 09:59:59. Ten hours is 40 parts
 * back to back, and any one of them failing fails the job — so the ceiling
 * is on the number of requests we are willing to chain, expressed as the
 * duration a person actually sees.
 *
 * A file whose duration cannot be read (WMA, a container the browser will
 * not decode) is NOT refused: unknown is not "too long", and the 75 MB size
 * limit still applies to it. */
export const MAX_DURATION_SECONDS = 10 * 60 * 60

export function exceedsMaxDuration(durationSeconds: number | null): boolean {
  return durationSeconds !== null && durationSeconds >= MAX_DURATION_SECONDS
}

export function needsChunking(durationSeconds: number | null): boolean {
  return durationSeconds !== null && durationSeconds > CHUNK_OVER_SECONDS
}

/**
 * The middle of the longest pause in an envelope, in seconds from its start,
 * or null when the audio never goes quiet for long enough.
 *
 * "Quiet enough for long enough" is not decided here: SILENCE_LEVEL and
 * MIN_PAUSE_FRAMES come from the subtitle segmenter, where they were tuned
 * against reference transcriptions. One definition of a pause, one place.
 */
export function longestPauseCentre(env: LoudnessEnvelope): number | null {
  const { rms, hop } = env
  if (rms.length === 0) return null

  const sorted = Float32Array.from(rms).sort()
  const median = sorted[Math.floor(sorted.length / 2)]
  if (!(median > 0)) return null
  const quiet = median * SILENCE_LEVEL

  let bestStart = -1
  let bestLength = 0
  let runStart = -1
  for (let i = 0; i <= rms.length; i++) {
    const isQuiet = i < rms.length && rms[i] <= quiet
    if (isQuiet) {
      if (runStart < 0) runStart = i
      continue
    }
    if (runStart >= 0) {
      const length = i - runStart
      if (length >= MIN_PAUSE_FRAMES && length > bestLength) {
        bestLength = length
        bestStart = runStart
      }
      runStart = -1
    }
  }
  if (bestStart < 0) return null
  return (bestStart + bestLength / 2) * hop
}

/**
 * Cut `file` into parts, or hand it back whole when it is short enough.
 *
 * ffmpeg is loaded only when a cut is actually needed: the audio path never
 * had that ~30 MB download before, and a 19-minute file must not start
 * paying for a feature it does not use.
 */
export async function cutIntoChunks(
  file: File,
  durationSeconds: number | null,
  onProgress: (p: ChunkProgress) => void = () => {},
): Promise<ChunkedAudio> {
  // Refused before this point by both callers; here so the rule cannot be
  // walked around by a future one
  if (exceedsMaxDuration(durationSeconds)) {
    throw new Error(`Recording longer than ${MAX_DURATION_SECONDS / 3600} hours`)
  }
  if (!needsChunking(durationSeconds)) return { chunks: [{ file, offset: 0 }], playback: null }
  const total = durationSeconds as number

  onProgress({ phase: 'load' })
  // Imported here, not at the top: a static import puts the ffmpeg wrapper in
  // the main bundle, which every visitor to the landing page downloads. The
  // thresholds and the pause search above are cheap and must stay importable
  // without dragging it along.
  const { createFFmpeg, readOutput } = await import('./ffmpegClient')

  const input = 'input-audio'
  const kbps = partBitrateKbps(file, total)
  const bitrate = `${kbps}k`
  const held: { ffmpeg: FFmpeg | null } = { ffmpeg: null }
  let execs = 0

  /** The current instance, replaced once it has done its share of work. */
  async function engine(): Promise<FFmpeg> {
    const current = held.ffmpeg
    if (current && execs < EXECS_PER_INSTANCE) return current
    if (current) current.terminate()
    const fresh = await createFFmpeg()
    // Read the file again rather than keeping one array around: writeFile
    // TRANSFERS the buffer to the worker, so a second instance handed the
    // same one gets "ArrayBuffer is already detached".
    await fresh.writeFile(input, new Uint8Array(await file.arrayBuffer()))
    held.ffmpeg = fresh
    execs = 0
    return fresh
  }

  try {
    const chunks: AudioChunk[] = []
    let start = 0
    let index = 0
    while (start < total - 0.5) {
      // The estimate is recomputed each time rather than fixed up front: a
      // cut can land up to two minutes early, so a fixed denominator was
      // overrun — the progress read "37 of 35".
      const expected = index + 1 + Math.ceil(Math.max(0, total - start - CHUNK_TARGET_SECONDS) / CHUNK_TARGET_SECONDS)
      onProgress({ phase: 'cut', done: index, total: expected })
      const remaining = total - start
      let end: number
      if (remaining <= CHUNK_OVER_SECONDS) {
        end = total
      } else {
        execs++
        end = start + (await findCut(await engine(), readOutput, input, start, remaining))
      }
      execs++
      const part = await slice(await engine(), readOutput, input, start, end - start, index, bitrate)
      if (!part) throw new Error('Could not split the audio into parts.')
      chunks.push({ file: part, offset: start })
      start = end
      index++
    }

    // Only a file the browser cannot seek gets a rendition. A mic recording,
    // a video's extracted track, a constant-bitrate mp3 — the browser seeks
    // all of those exactly, and building one would cost them quality for
    // nothing. A WMA is the extreme case of the rule rather than an
    // exception to it: the browser cannot play it at all, so without the
    // rendition the player has no audio — and the parts are MP3, and are
    // already here.
    let playback: PlaybackAudio | null = null
    if (worthJoining(chunks) && (isWma(file) || (await seekIsUnreliable(file)))) {
      // The cutting instance still holds the whole recording inside wasm, and
      // the copy needs room for the concatenation — so it gets a bare
      // instance rather than sharing that one.
      held.ffmpeg?.terminate()
      held.ffmpeg = await createFFmpeg()
      playback = await buildPlayback(held.ffmpeg, readOutput, chunks, kbps * 1000)
    }
    return { chunks, playback }
  } finally {
    // Frees the worker and all wasm memory — important on mobile
    held.ffmpeg?.terminate()
  }
}

/** Above this the rendition is skipped and the player falls back to the
 * original file, seeking as best it can: the parts, the concatenation and
 * ffmpeg's copy of it are all in memory at once, and a phone is not the
 * place to find the ceiling. Ten hours of the 64 kbit/s worst case is
 * 288 MB, so this is a real limit, not a formality. */
const MAX_PLAYBACK_BYTES = 120 * 1024 * 1024

/**
 * Glue the parts back together into something `<audio>` can seek.
 *
 * The concatenation is already the right bytes — the work here is the `Info`
 * header. Without it the browser seeks a headerless stream no better than it
 * seeks the VBR original; with it, a click lands within 0.08 s. It is a
 * STREAM COPY, so nothing is decoded or re-encoded: the parts keep exactly
 * the audio the gateway was given.
 *
 * Best-effort throughout. A rendition that cannot be built is not a reason
 * to fail an upload that has already succeeded — the player simply gets the
 * original file, which is what it had before any of this.
 */
function worthJoining(chunks: AudioChunk[]): boolean {
  return chunks.length > 1 && chunks.reduce((n, c) => n + c.file.size, 0) <= MAX_PLAYBACK_BYTES
}

async function buildPlayback(
  ffmpeg: FFmpeg,
  readOutput: (f: FFmpeg, path: string) => Promise<Uint8Array | null>,
  chunks: AudioChunk[],
  bitrateBps: number,
): Promise<PlaybackAudio | null> {
  const joined = 'playback-in.mp3'
  const out = 'playback.mp3'
  try {
    const blob = new Blob(chunks.map((c) => c.file), { type: 'audio/mpeg' })
    await ffmpeg.writeFile(joined, new Uint8Array(await blob.arrayBuffer()))
    const rc = await ffmpeg.exec(['-i', joined, '-c', 'copy', '-write_xing', '1', out])
    const data = rc === 0 ? await readOutput(ffmpeg, out) : null
    if (!data) return null
    return {
      file: new File([data.slice()], 'playback.mp3', { type: 'audio/mpeg' }),
      spans: spansFromParts(
        chunks.map((c) => ({ offset: c.offset, size: c.file.size })),
        bitrateBps,
      ),
    }
  } catch {
    return null
  } finally {
    await ffmpeg.deleteFile(joined).catch(() => {})
    await ffmpeg.deleteFile(out).catch(() => {})
  }
}

/**
 * A playable copy of a file the browser cannot play at all, for a recording
 * short enough not to be cut.
 *
 * The cut path already hands the player MP3 parts joined together; a WMA
 * below the threshold never goes through it, so it had nothing the player
 * could open. This is the same encoder `slice` runs for a part — mono,
 * constant bitrate, no tags — over the whole file: one part, starting at 0,
 * so the time map is the identity and every cue plays where it says.
 *
 * It runs BESIDE the upload rather than before it (useTranscription), so a
 * short file pays nothing on its critical path: a few seconds of encoding
 * against the tens the gateway spends recognising. Best-effort like every
 * rendition — null leaves the player without audio, which is what it had
 * before — and an abort tears the instance down instead of finishing work
 * nobody will play.
 */
export async function buildWholeFilePlayback(
  file: File,
  durationSeconds: number | null,
  signal?: AbortSignal,
): Promise<PlaybackAudio | null> {
  if (signal?.aborted) return null
  let ffmpeg: FFmpeg | null = null
  const stop = () => ffmpeg?.terminate()
  signal?.addEventListener('abort', stop, { once: true })
  try {
    // Inside the try like everything else here: a chunk that fails to load
    // is one more reason there is no copy, not a rejection nobody awaits
    const { createFFmpeg, readOutput } = await import('./ffmpegClient')
    ffmpeg = await createFFmpeg()
    if (signal?.aborted) return null
    const input = 'input-audio'
    await ffmpeg.writeFile(input, new Uint8Array(await file.arrayBuffer()))
    const kbps = durationSeconds ? partBitrateKbps(file, durationSeconds) : MAX_PART_KBPS
    // To the end, whatever the header said: the duration arrives rounded to
    // the second, and a -t taken from it would drop up to half a second of
    // the last word. A -t past the end is simply where the file stops.
    const part = await slice(ffmpeg, readOutput, input, 0, MAX_DURATION_SECONDS, 0, `${kbps}k`)
    if (!part || signal?.aborted) return null
    return { file: part, spans: spansFromParts([{ offset: 0, size: part.size }], kbps * 1000) }
  } catch {
    return null
  } finally {
    signal?.removeEventListener('abort', stop)
    ffmpeg?.terminate()
  }
}

/** How long the next part should be, measured from `start`. */
async function findCut(
  ffmpeg: FFmpeg,
  readOutput: (f: FFmpeg, path: string) => Promise<Uint8Array | null>,
  input: string,
  start: number,
  remaining: number,
): Promise<number> {
  const searchFrom = Math.min(CHUNK_SEARCH_FROM, Math.max(0, remaining - 1))
  const searchLength = Math.max(1, Math.min(CHUNK_TARGET_SECONDS - searchFrom, remaining - searchFrom))
  const windowPath = 'cut-window.wav'
  try {
    const rc = await ffmpeg.exec([
      '-ss', String(start + searchFrom),
      '-i', input,
      '-t', String(searchLength),
      '-ac', '1', '-ar', String(WINDOW_RATE), '-c:a', 'pcm_s16le', '-bitexact',
      windowPath,
    ])
    const data = rc === 0 ? await readOutput(ffmpeg, windowPath) : null
    if (data) {
      const env = await envelopeOf(new Blob([data.slice()], { type: 'audio/wav' }), CHUNK_TARGET_SECONDS)
      const centre = env ? longestPauseCentre(env) : null
      if (centre !== null) return searchFrom + centre
    }
  } catch {
    // A window we could not read is not a reason to fail the upload — the
    // hard stop below is exactly what it is there for
  } finally {
    await ffmpeg.deleteFile(windowPath).catch(() => {})
  }
  // Music, or speech that never stops: cut on the clock.
  return CHUNK_TARGET_SECONDS
}

async function slice(
  ffmpeg: FFmpeg,
  readOutput: (f: FFmpeg, path: string) => Promise<Uint8Array | null>,
  input: string,
  start: number,
  length: number,
  index: number,
  bitrate: string,
): Promise<File | null> {
  const out = `part-${index}.mp3`
  // -ss BEFORE -i: seek first, then decode. What keeps the cut accurate is
  // `-c:a libmp3lame` — re-encoding — not the position of -ss; frame snapping
  // is a STREAM COPY problem. Measured on a real 8 h file: cutting at 8:00:00
  // takes 0.83 s this way against 7.04 s with -ss after -i, and the two
  // outputs align to 0 samples.
  //
  // The cost of getting that wrong was not just slowness. With -ss after -i
  // every part decodes the whole recording before it, so part 35 decoded
  // eight hours to reach its start — O(n²) over the file, and enough churn
  // through one ffmpeg instance that a 35-part cut died on "memory access out
  // of bounds" two parts from the end.
  //
  // `-map_metadata -1 -id3v2_version 0` drops the tag ffmpeg would otherwise
  // copy from the source — 348 bytes of album and artist on every part, which
  // is both pointless on the wire and an ID3 header in the middle of the
  // stream once the parts are concatenated for playback.
  const rc = await ffmpeg.exec([
    '-ss', String(start),
    '-i', input,
    '-t', String(length),
    '-vn', '-ac', '1', '-c:a', 'libmp3lame', '-b:a', bitrate, '-bitexact',
    '-map_metadata', '-1', '-id3v2_version', '0',
    out,
  ])
  const data = rc === 0 ? await readOutput(ffmpeg, out) : null
  await ffmpeg.deleteFile(out).catch(() => {})
  if (!data) return null
  return new File([data.slice()], out, { type: 'audio/mpeg' })
}
